import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPricing, getCachedPricing, getLandAcreageConfig, landExtraAcres } from '../utils/pricing';
import { updatePageMeta } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';
import './Pricing.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * /pricing — every number on this page comes from the API:
 *   packages, add-ons, surcharge, gallery hosting  → GET /api/pricing/all
 *   credit packs                                    → GET /api/credit-packs
 * Admin price overrides therefore show here the moment they are saved.
 * pricingpage.test.js fails the build if a dollar figure is typed into this
 * file. (The homepage and blog linked to /pricing for months while it 404d.)
 */
const money = (n) => {
  const v = Number(n) || 0;
  return `$${v % 1 === 0 ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function Pricing() {
  // Starts from the prices the server printed (#urp-pricing), so the cards are
  // there on first paint instead of jumping in after a fetch (2026-10-17).
  const [pricing, setPricing] = useState(() => getCachedPricing());
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    updatePageMeta(metaFor('/pricing'));
    let alive = true;
    getPricing().then(p => { if (alive) setPricing(p); });
    fetch(`${API_URL}/credit-packs`)
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (alive && Array.isArray(j?.data)) setPacks(j.data); })
      .catch(() => { /* the section falls back to a link */ });
    return () => { alive = false; };
  }, []);

  const allPackages = (pricing?.packages || []).filter(p => p && p.active !== false);
  // Land packages (2026-10-11) get their own section below the home packages.
  const packages = allPackages.filter(p => p.category !== 'land');
  const landPackages = allPackages.filter(p => p.category === 'land');
  // Land is priced by size (2026-10-12); every figure comes from the config.
  const landCfg = (pricing && pricing.landAcreage) || getLandAcreageConfig();
  const landBase = landPackages[0] ? Number(landPackages[0].price) || 0 : 0;
  const landPrice = (acres) => landBase + landExtraAcres(acres, landCfg) * landCfg.pricePerAcre;
  const renderCard = (pkg) => {
    const features = (pkg.features || []).filter(f => !String(f).startsWith('*'));
    const notes = (pkg.features || []).filter(f => String(f).startsWith('*')).map(f => String(f).slice(1));
    return (
      <article key={pkg._id} className={`pp-card${pkg.popular ? ' is-popular' : ''}`}>
        {pkg.popular && <p className="pp-flag">Most booked</p>}
        <h2>{String(pkg.name || '').replace(/ Package$/, '')}</h2>
        <p className="pp-price">{money(pkg.price)}</p>
        <ul>
          {features.map(f => <li key={f}>{f}</li>)}
        </ul>
        {notes.map(n => <p key={n} className="pp-note">{n}</p>)}
        <Link to={`/book?package=${encodeURIComponent(pkg._id)}`} className={`btn ${pkg.popular ? 'btn-primary' : 'btn-outline'}`}>
          Book {String(pkg.name || '').replace(/ Package$/, '')}
        </Link>
      </article>
    );
  };
  const addOns = (pricing?.addOns || []).filter(a => a && !a.retired && Number(a.price) > 0);
  const hostingDays = Number(pricing?.mediaExpiryDays) > 0 ? pricing.mediaExpiryDays : null;

  return (
    <div className="pricing-page">
      <header className="pp-head">
        <div className="container">
          <h1>Pricing</h1>
          <p className="pp-lede">Fixed package prices, published in full. Pick a package, add what the listing needs, and see the total before you book.</p>
        </div>
      </header>

      <section className="pp-packages">
        <div className="container">
          {!pricing && <p className="pp-loading">Loading current prices…</p>}
          <div className="pp-grid">
            {packages.map(renderCard)}
          </div>
        </div>
      </section>

      {landPackages.length > 0 && (
        <section className="pp-packages pp-land" id="land">
          <div className="container">
            <h2 className="pp-section-title">Land &amp; lots</h2>
            <p className="pp-sub">Aerial photos covering the whole parcel with the boundary drawn on, plus ground photos. <Link to="/services/land">How land shoots work</Link></p>
            <div className="pp-grid">
              {landPackages.map(renderCard)}
              <article className="pp-card pp-card-quote">
                <h2>Priced by size</h2>
                <p className="pp-quote-text">
                  {money(landBase)} covers the first acre. Each additional acre, or part of one, adds {money(landCfg.pricePerAcre)} and at least {landCfg.aerialPhotosPerAcre} more aerial photos. Any size books online.
                </p>
                <ul>
                  {[0.5, 2, 5, 10].map(a => <li key={a}>{a === 0.5 ? '½' : a} acre{a > 1 ? 's' : ''} — {money(landPrice(a))}</li>)}
                </ul>
              </article>
            </div>
          </div>
        </section>
      )}

      {addOns.length > 0 && (
        <section className="pp-addons">
          <div className="container">
            <h2>Add-ons</h2>
            <p className="pp-sub">Add any of these to a package, or book them on their own.</p>
            <div className="pp-table-wrap">
              <table>
                <tbody>
                  {addOns.map(a => (
                    <tr key={a.id}>
                      <th scope="row">{a.name}<span>{a.description}</span></th>
                      <td>{money(a.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <section className="pp-credit">
        <div className="container">
          <h2>Prepaid credit</h2>
          <p className="pp-sub">Put money on account and get bonus credit on top. It never expires and works on every package and add-on.</p>
          {packs.length > 0 && (
            <div className="pp-packs">
              {packs.map(p => (
                <div key={p.id} className="pp-pack">
                  <p className="pp-pack-pay">Pay {money(p.price)}</p>
                  <p className="pp-pack-get">Get {money(p.totalCredit)}</p>
                  {p.bonusAmount > 0 && <p className="pp-pack-bonus">{money(p.bonusAmount)} bonus</p>}
                </div>
              ))}
            </div>
          )}
          <Link to="/credit" className="btn btn-outline">How credit works</Link>
        </div>
      </section>

      <section className="pp-fine">
        <div className="container">
          <h2>Good to know</h2>
          <ul>
            <li>Pay online when you book, or later from your invoice. Your gallery unlocks as soon as it is paid.</li>
            <li>A travel fee applies outside the free service area. You see it before you confirm.</li>
            {hostingDays && <li>Galleries stay online for {hostingDays} days after delivery, and can be extended.</li>}
            <li>Brokerages and high-volume teams can set up a shared volume rate. <Link to="/contact">Get in touch</Link>.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
